/**
 * Expense category prediction — keyword tier only.
 *
 * Web's prediction flow used to be a ML model + keyword hybrid with
 * a stack of disambiguation rules layered on top to patch ML
 * mispredictions. After stages 1-10 made the keyword detector
 * locale-aware and brand-rich, the ML model became vestigial — its
 * "long tail" coverage is now handled by the cloud LLM tier (Claude
 * Haiku via `/expenses/predict-category`, mirrors mobile).
 *
 * The ranking itself lives in `@splitfiesta/core`
 * (`predictExpenseCategoryFromKeywords`) so web and mobile pick the
 * same winner, apply the same taxonomy guard and haircut ties the
 * same way. The hook layer (`useCategoryPrediction`) calls
 * `predictFromKeyword` first, then falls through to the cloud LLM via
 * `ApiClientService`, then to `other`.
 *
 * Keeping the singleton form for callsite stability — callers still
 * write `expenseCategoryService.predictFromKeyword(...)`.
 */
import {
  KEYWORD_MIN_CATEGORY_CONFIDENCE,
  KEYWORD_MIN_SUBCATEGORY_CONFIDENCE,
  type KeywordPrediction,
  MIN_PREDICTION_TITLE_CHARS,
  predictExpenseCategoryAcrossLanguages,
} from '@splitfiesta/core';

type CategoryPredictionResult = KeywordPrediction;

/** Titles shorter than this are not classified by any tier (shared with mobile). */
const MIN_TITLE_CHARS = MIN_PREDICTION_TITLE_CHARS;
const MIN_CATEGORY_CONFIDENCE = KEYWORD_MIN_CATEGORY_CONFIDENCE;
const MIN_SUBCATEGORY_CONFIDENCE = KEYWORD_MIN_SUBCATEGORY_CONFIDENCE;

class ExpenseCategoryService {
  /**
   * Keyword tier for `title` in `language`. Returns `null` when
   * nothing matched with enough confidence — the hook should then
   * try the cloud LLM. Pure and synchronous; no I/O.
   */
  predictFromKeyword(
    title: string,
    language: string | readonly (string | undefined)[]
  ): CategoryPredictionResult | null {
    // Trim once up front so the detectors see canonical input
    // regardless of who called us — the hook already trims, but
    // a service method shouldn't depend on its callers remembering.
    const trimmed = title?.trim() ?? '';
    if (trimmed.length < MIN_TITLE_CHARS) {
      return null;
    }
    return predictExpenseCategoryAcrossLanguages(
      trimmed,
      typeof language === 'string' ? [language] : language
    );
  }
}

export const expenseCategoryService = new ExpenseCategoryService();
export { MIN_CATEGORY_CONFIDENCE, MIN_SUBCATEGORY_CONFIDENCE, MIN_TITLE_CHARS };
export type { CategoryPredictionResult };
